import { useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, ChevronLeft, ChevronRight, CirclePlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { SpellIssue } from '@/lib/spell-check/spell-check-state'
import { cn } from '@/utils'
import { WORD_RULE_MESSAGES } from './constants/word-rule-messages'
import { isTooLongForDictionary, popoverWordRuleMessage } from './helpers/word-rule-message'

export interface SpellFixPopoverContentProps {
  issue: SpellIssue
  /** Suggestions for the word, computed when this popover opens. */
  getSuggestions: (word: string) => string[]
  occurrenceIndex: number
  onOccurrenceIndexChange: (index: number) => void
  onChange: (replacement: string) => void
  onChangeAll: (replacement: string) => void
  onIgnore: () => void
  onIgnoreAll: () => void
  /** Adds the flagged word; settles once the backend has answered. */
  onAddToDictionary: () => Promise<void>
  /**
   * Changes this instance to the typed word and adds that word to the dictionary. Settles once the
   * backend has answered, with whether the instance was changed.
   */
  onChangeAndAdd: (replacement: string) => Promise<boolean>
}

const ADD_RULE_MESSAGE_ID = 'spell-fix-add-rule'

/** Muted text-button used for the inline "Change" / "Change all" actions (Figma Button/Link sm). */
const INLINE_ACTION_CLASS =
  'rounded-sm text-xs font-medium text-muted-foreground no-underline hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:underline disabled:pointer-events-none disabled:opacity-50'

/**
 * Body of the spell-fix popover (Figma "Spell Fix Popover — Combined"). Occurrence-aware: the
 * instance stepper, per-suggestion "Change all" links and the Change All dropdown only render
 * when the flagged word appears more than once. The dictionary button adds the flagged word, and
 * reads Change & Add to Dictionary while a correction is typed (SC-16). The actions are locked
 * while a save to the dictionary runs, so a second press cannot send the word twice.
 */
export function SpellFixPopoverContent({
  issue,
  getSuggestions,
  occurrenceIndex,
  onOccurrenceIndexChange,
  onChange,
  onChangeAll,
  onIgnore,
  onIgnoreAll,
  onAddToDictionary,
  onChangeAndAdd,
}: SpellFixPopoverContentProps) {
  const [correction, setCorrection] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  // State lags a render behind, so a second press in the same tick reads this ref instead.
  const isSavingRef = useRef(false)
  const suggestions = useMemo(() => getSuggestions(issue.word), [getSuggestions, issue.word])
  const occurrenceCount = issue.occurrences.length
  const isRepeated = occurrenceCount > 1
  const occurrence = issue.occurrences[occurrenceIndex]
  const isResolved = occurrence?.status !== 'open'
  const isBusy = isResolved || isSaving
  const trimmedCorrection = correction.trim()
  const isTypingCorrection = trimmedCorrection !== ''
  // Figma "Word Rules / Add to Dictionary in the Fix Popover": a word the dictionary can't hold. With
  // a correction typed it is the correction that is added, so it is the one held to the rules.
  const ruleMessage = isTypingCorrection
    ? popoverWordRuleMessage(trimmedCorrection)
    : isTooLongForDictionary(issue.word)
      ? WORD_RULE_MESSAGES.popoverTooLong
      : null
  const isAddDisabled = isSaving || ruleMessage !== null || (isTypingCorrection && isResolved)

  const submitCorrection = () => {
    if (!isTypingCorrection || isBusy || isSavingRef.current) return

    onChange(trimmedCorrection)
    setCorrection('')
  }

  const runSave = async (save: () => Promise<void>) => {
    if (isSavingRef.current) return

    isSavingRef.current = true
    setIsSaving(true)

    try {
      await save()
    } finally {
      isSavingRef.current = false
      setIsSaving(false)
    }
  }

  const addToDictionary = () => {
    if (isTypingCorrection) {
      // The typed word stays in the box when the save fails, so the user can try again.
      void runSave(async () => {
        if (await onChangeAndAdd(trimmedCorrection)) setCorrection('')
      })

      return
    }

    void runSave(onAddToDictionary)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between pr-2">
        <p className="text-base leading-none font-bold text-destructive">{issue.word}</p>
        {isRepeated && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Previous instance"
              disabled={occurrenceIndex === 0}
              onClick={() => onOccurrenceIndexChange(occurrenceIndex - 1)}
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Next instance"
              disabled={occurrenceIndex >= occurrenceCount - 1}
              onClick={() => onOccurrenceIndexChange(occurrenceIndex + 1)}
            >
              <ChevronRight />
            </Button>
            <span className="flex items-center gap-1 text-xs leading-none text-foreground">
              {occurrenceIndex + 1}/{occurrenceCount}
              {isResolved && <Check className="size-3 text-success" aria-label="Corrected" />}
            </span>
          </div>
        )}
      </div>

      {suggestions.length === 0 && (
        // A 24px block as in Figma "Spell Fix Popover — Combined (No suggestions)": `leading-4`
        // keeps the line at 16px, where `text-sm` alone makes it 20px and the block 28px.
        <p className="px-2 py-1 text-sm leading-4 text-muted-foreground">
          No suggestions. Type a correction below.
        </p>
      )}

      {suggestions.length > 0 && (
        <ul className="flex flex-col gap-1">
          {suggestions.map((suggestion) => (
            <li
              key={suggestion}
              className={cn(
                'group flex items-center justify-between rounded-sm px-2 py-1.5 hover:bg-accent focus-within:bg-accent',
                isResolved && 'pointer-events-none opacity-50'
              )}
            >
              <button
                type="button"
                className="flex-1 text-left text-sm leading-none text-foreground focus-visible:outline-none"
                disabled={isBusy}
                onClick={() => onChange(suggestion)}
              >
                {suggestion}
              </button>
              {isRepeated && (
                <button
                  type="button"
                  className={cn(INLINE_ACTION_CLASS, 'group-hover:underline')}
                  disabled={isBusy}
                  onClick={() => onChangeAll(suggestion)}
                >
                  Change all
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between">
        <Input
          className="w-[180px]"
          placeholder="Type a correction"
          aria-label="Type a correction"
          value={correction}
          disabled={isResolved}
          onChange={(e) => setCorrection(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitCorrection()
          }}
        />
        <div className={cn('flex items-center gap-1', !isRepeated && 'pr-2')}>
          <button
            type="button"
            className={INLINE_ACTION_CLASS}
            disabled={!isTypingCorrection || isBusy}
            onClick={submitCorrection}
          >
            Change
          </button>
          {isRepeated && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="More change options"
                  disabled={!isTypingCorrection || isBusy}
                >
                  <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => {
                    onChangeAll(trimmedCorrection)
                    setCorrection('')
                  }}
                >
                  Change all
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <Button
        variant="outline"
        size="sm"
        className="w-full border-dashed text-xs"
        disabled={isAddDisabled}
        // Figma "Failure Notifications / Saving State": a spinner and "Adding…" while the word saves.
        isLoading={isSaving}
        aria-describedby={ruleMessage ? ADD_RULE_MESSAGE_ID : undefined}
        onClick={addToDictionary}
      >
        {!isSaving && <CirclePlus />}
        <span className="truncate">
          {isSaving
            ? 'Adding…'
            : isTypingCorrection
              ? 'Change & Add to Dictionary'
              : `Add "${issue.word}" to Dictionary`}
        </span>
      </Button>
      {ruleMessage && (
        <p id={ADD_RULE_MESSAGE_ID} className="text-xs text-muted-foreground">
          {ruleMessage}
        </p>
      )}

      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" disabled={isBusy} onClick={onIgnore}>
          Ignore
        </Button>
        <Button variant="ghost" size="sm" disabled={isSaving} onClick={onIgnoreAll}>
          Ignore all
        </Button>
      </div>
    </div>
  )
}
